/**
 * TOSS Web Application - Equipment Term Popover (a.k.a. The Meme Button)
 *
 * The customer renamed "Энергетическая установка" three times in a row and
 * the latest feedback was literally ")))))). Так что мы дали возможность
 * переименовывать её прямо из шапки приложения.
 *
 * Backed by the i18n store (key `equipment.label`). Reads and writes are
 * scoped to this single key — if we ever need to expose more keys we can
 * generalise this component.
 */

import { useMemo, useRef, useState } from 'react';
import { Tag } from 'lucide-react';
import { Button, DropdownPortal } from '@/components/ui';
import { TopBarAction } from '@/components/topbar';
import { I18N_DEFAULTS } from '@/i18n/defaults';
import { useI18nStore, useTerm } from '@/store/i18nStore';

const KEY = 'equipment.label' as const;
const DEFAULT_VALUE = I18N_DEFAULTS[KEY];

const JOKE_TITLES = [
  'Так как же правильно?..',
  'Владимир переименовал. И ты можешь.',
  'Заказчик передумал. Снова.',
  'Терминологический кризис, эпизод 47',
  'Энергетическая… дальше как хочешь',
  'Назови как нравится — мы потерпим',
  'Освежим этот ваш термин?',
];

interface Preset {
  value: string;
  hint?: string;
}

const PRESETS: Preset[] = [
  // Подписи показывают на УМОЛЧАНИЕ ключа, и оно проверено по истории 31.08.2026: «установка»
  // стоит с мая, «оборудование» — имя, от которого ушли. Разворот, сделанный в тот же день,
  // отменён владельцем после счёта живых строк.
  { value: 'Энергетическая установка', hint: 'по умолчанию' },
  { value: 'Энергетическое оборудование', hint: 'было до этого' },
  { value: 'Энергоустановка', hint: 'покороче' },
  { value: 'Объект энергоснабжения', hint: 'бюрократично' },
  { value: 'ЭУ', hint: 'для смелых' },
  { value: 'Эта Штука', hint: 'честно' },
];

export function EquipmentTermPopover(): JSX.Element {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState('');

  const current = useTerm(KEY);
  const setOverride = useI18nStore((s) => s.setOverride);
  const clearOverride = useI18nStore((s) => s.clearOverride);
  const hasOverride = useI18nStore((s) => KEY in s.overrides);

  // Pick a fresh joke title every time the popover opens.
  const title = useMemo(() => {
    if (!isOpen) return JOKE_TITLES[0];
    return JOKE_TITLES[Math.floor(Math.random() * JOKE_TITLES.length)];
  }, [isOpen]);

  const handleClose = (): void => {
    setIsOpen(false);
    setDraft('');
  };

  const handlePreset = (value: string): void => {
    if (value === DEFAULT_VALUE) {
      clearOverride(KEY);
    } else {
      setOverride(KEY, value);
    }
  };

  const handleSaveCustom = (): void => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (trimmed === DEFAULT_VALUE) {
      clearOverride(KEY);
    } else {
      setOverride(KEY, trimmed);
    }
    setDraft('');
  };

  const handleReset = (): void => {
    clearOverride(KEY);
    setDraft('');
  };

  return (
    <>
      {/* The shared top-bar control, like every other icon on the bar (2026-08-26). The accent
          colour it used to carry went with it: inside the tools group this is one utility among
          three, and it was the only tinted glyph on a bar of grey ones. */}
      <TopBarAction
        ref={triggerRef}
        icon={Tag}
        label="Переименовать «Энергетическую установку»"
        title="Как назовём сегодня?"
        active={isOpen}
        onClick={() => setIsOpen((v) => !v)}
      />

      <DropdownPortal
        isOpen={isOpen}
        onClose={handleClose}
        triggerRef={triggerRef}
        placement="bottom-end"
        offset={12}
        className="app-surface app-surface-outlined w-[360px] shadow-[var(--shadow-overlay)] animate-fade-in"
      >
        <div className="p-4">
          {/* Header */}
          <div className="flex items-start gap-2 mb-3">
            <span className="text-[var(--color-accent-500)] mt-0.5">
              <Tag className="w-5 h-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                {title}
              </p>
              <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">
                Сейчас отображается:{' '}
                <span className="font-medium text-[var(--color-text-primary)]">
                  «{current}»
                </span>
              </p>
            </div>
          </div>

          {/* Presets */}
          <p className="text-xs uppercase tracking-wide text-[var(--color-text-secondary)] mb-2">
            Заготовки от Владимира
          </p>
          <div className="space-y-1.5 mb-4">
            {PRESETS.map((preset) => {
              const isActive = preset.value === current;
              return (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => handlePreset(preset.value)}
                  className={[
                    'w-full text-left rounded-lg px-3 py-2 text-sm transition-colors flex items-center justify-between gap-3',
                    isActive
                      ? 'bg-[var(--color-accent-500)]/10 text-[var(--color-accent-600)] ring-1 ring-[var(--color-accent-500)]/40'
                      : 'bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)] hover:bg-[var(--color-bg-tertiary)]',
                  ].join(' ')}
                >
                  <span className="truncate">{preset.value}</span>
                  {preset.hint && (
                    <span className="text-3xs uppercase tracking-wide text-[var(--color-text-secondary)] flex-shrink-0">
                      {preset.hint}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Custom */}
          <p className="text-xs uppercase tracking-wide text-[var(--color-text-secondary)] mb-2">
            А как назовёте вы?
          </p>
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSaveCustom();
                }
              }}
              placeholder="Свой вариант…"
              className="flex-1 min-w-0 h-[var(--h-control)] px-3 text-touch md:text-md bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
              maxLength={80}
            />
            <Button
              size="sm"
              variant="primary"
              onClick={handleSaveCustom}
              disabled={!draft.trim()}
            >
              Применить
            </Button>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-2 border-t border-[var(--color-border-light)]">
            <span className="text-2xs text-[var(--color-text-secondary)]">
              По умолчанию: «{DEFAULT_VALUE}»
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleReset}
              disabled={!hasOverride}
            >
              Сбросить
            </Button>
          </div>
        </div>
      </DropdownPortal>
    </>
  );
}

export default EquipmentTermPopover;
